import { Observable } from "rxjs";
import { isEqual as deepEqual } from "lodash";

import st from "lib/utils/index";
import { createAlert, removeAlert } from "modules/app/actions";

const getErrorMessageFromSubmitError = (error) => {
  return st.api.findResponseErrorMessage(error);
};

export default function createFormInstance({
  INITIALIZE_MODEL,
  UPDATE_MODEL,
  VALIDATE,
  SUBMIT,
  FETCH_SUBMIT_START,
  FETCH_SUBMIT_SUCCESS,
  FETCH_SUBMIT_FAILURE,
  stateSelector = (state) => {
    throw new Error("undefined stateSelector in createFormInstance");
  },
  stateKey = "form",
  successAlertContent = () => I18n.t("papp.common.submit_success_message"),
  successAlertTimeout,
  withSuccessAlert = !!successAlertContent,
  withFailureAlert = true,
  updateModelAfterSubmit = true,
  mergeFailureAlertIntoErrors = false,
  isDirty = (a, b) => !deepEqual(a, b),
  validate = (state, errors, { submitValidate }) => errors,
  submitModel = (state, store) => Observable.of(state.model),
}) {
  const initialState = {
    initialModel: null,
    model: null,
    dirty: false,
    valid: true,
    errors: new st.form.ValidationErrors(),
    submitDone: false,
    submitLoading: false,
    submitFailed: false,
  };

  const reducersMap = {
    [INITIALIZE_MODEL]: (state, { initialModel, model = initialModel }) => {
      return {
        ...state,
        [stateKey]: {
          ...initialState,
          initialModel,
          model,
        },
      };
    },

    [UPDATE_MODEL]: (state, { model }) => ({
      ...state,
      [stateKey]: {
        ...state[stateKey],
        model,
        dirty: isDirty(model, state.initialModel),
      },
    }),

    [VALIDATE]: (state, { submitValidate }) => {
      const errors = new st.form.ValidationErrors();
      validate(state, errors, { submitValidate });
      return {
        ...state,
        [stateKey]: {
          ...state[stateKey],
          valid: errors.valid,
          errors,
        },
      };
    },

    [FETCH_SUBMIT_START]: (state) => ({
      ...state,
      [stateKey]: {
        ...state[stateKey],
        submitLoading: true,
        submitFailed: false,
        submitDone: false,
      },
    }),

    [FETCH_SUBMIT_SUCCESS]: (state, { model }) => ({
      ...state,
      [stateKey]: {
        ...state[stateKey],
        ...(updateModelAfterSubmit && model
          ? {
              initialModel: model,
              model,
              dirty: false,
            }
          : {}),
        submitLoading: false,
        submitDone: true,
      },
    }),

    [FETCH_SUBMIT_FAILURE]: (state, { error }) => ({
      ...state,
      [stateKey]: {
        ...state[stateKey],
        submitLoading: false,
        submitFailed: true,
        ...(mergeFailureAlertIntoErrors && error
          ? {
              errors: new st.form.ValidationErrors([["base", getErrorMessageFromSubmitError(error)]]),
            }
          : {}),
      },
    }),
  };

  const epics = [
    (actions$, store) =>
      actions$
        .ofType(SUBMIT)
        .filter(() => {
          const state = stateSelector(store.getState());
          const { submitLoading } = state[stateKey];
          return !submitLoading;
        })
        .switchMap(() => {
          setTimeout(() => {
            store.dispatch({ type: VALIDATE, submitValidate: true });
          });
          return actions$.ofType(VALIDATE).take(1);
        })
        .filter(() => {
          const state = stateSelector(store.getState());
          const { valid } = state[stateKey];
          return valid;
        })
        .switchMap(() => {
          const state = stateSelector(store.getState());
          const submit$ = submitModel(state, store);
          return Observable.concat(
            Observable.of({ type: FETCH_SUBMIT_START }),
            withFailureAlert ? Observable.of(removeAlert({ uuid: FETCH_SUBMIT_FAILURE })) : Observable.empty(),
            submit$
              .flatMap((model) =>
                Observable.concat(
                  Observable.of({ type: FETCH_SUBMIT_SUCCESS, model }),
                  withSuccessAlert
                    ? Observable.of(
                        createAlert({
                          type: "success",
                          content: successAlertContent(),
                          timeout: successAlertTimeout,
                        })
                      )
                    : Observable.empty()
                )
              )
              .catch((error) => {
                st.api.handleError(error, store);
                return Observable.concat(
                  Observable.of({ type: FETCH_SUBMIT_FAILURE, error }),

                  withFailureAlert
                    ? Observable.of(
                        createAlert({
                          uuid: FETCH_SUBMIT_FAILURE,
                          type: "error",
                          content: getErrorMessageFromSubmitError(error),
                          timeout: false,
                        })
                      )
                    : Observable.empty()
                );
              })
          );
        }),
  ];

  const actions = {
    initializeModel(initialModel, model) {
      return { type: INITIALIZE_MODEL, initialModel, model };
    },

    updateModel(model) {
      return { type: UPDATE_MODEL, model };
    },

    validate({ submitValidate = false } = {}) {
      return { type: VALIDATE, submitValidate };
    },

    submit() {
      return { type: SUBMIT };
    },
  };

  return {
    initialState,
    reducersMap,
    actions,
    epics,
  };
}



// WEBPACK FOOTER //
// ./src/modules/utils/createFormInstance.js